// src/modulos/Parametros/PrefixPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

const PREFIX_COLLECTION = 'parameters/data/sufixos';
const ITEMS_PER_PAGE = 10;

const PrefixPage = () => {
    const can = usePagePermissions('prefixos');

    // Estados
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: prefixes,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(PREFIX_COLLECTION, 'name');

    // ============================================================
    // 1. DEFINIR filteredPrefixes ANTES dos callbacks que o usam
    // ============================================================
    const filteredPrefixes = useMemo(() => {
        if (!prefixes) return [];
        if (!searchTerm) return prefixes;

        return prefixes.filter(p =>
            p.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [prefixes, searchTerm]);

    const totalFilteredItems = filteredPrefixes.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentPrefixes = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredPrefixes.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredPrefixes, currentPage]);

    // Reset do Formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(false);
    }, []);

    const handleCloseModal = useCallback(() => {
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete (AGORA filteredPrefixes já está definido)
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Prefixo "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                // Usar filteredPrefixes (já definido acima)
                const remainingItems = filteredPrefixes.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError(result.error || 'Erro ao excluir prefixo');
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar prefixo. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, filteredPrefixes, currentPage]);

    // Submissão do Formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError('O nome do prefixo é obrigatório.');
            return;
        }

        const prefixName = nameInput.trim().toUpperCase();

        // Verificar duplicidade
        const exists = prefixes?.some(p =>
            p.name === prefixName && (!editingItem || p.id !== editingItem.id)
        );

        if (exists) {
            setFormError('Este prefixo já existe. Por favor, use um nome diferente.');
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: prefixName,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            const result = editingItem
                ? await updateDocument(editingItem.id, data)
                : await addDocument(data);

            if (result.success) {
                setFormSuccess(editingItem ? 'Prefixo editado com sucesso!' : 'Prefixo adicionado com sucesso!');
                setTimeout(() => {
                    resetForm();
                    setCurrentPage(1);
                }, 1500);
            } else {
                setFormError(result.error || 'Erro ao salvar prefixo.');
            }
        } catch (err) {
            setFormError('Erro de conexão. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, prefixes, addDocument, updateDocument, resetForm]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Novo Prefixo'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome do Prefixo <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value.toUpperCase())}
                        placeholder="Ex: MAT, EMB, QUI, PROD..."
                        className={styles.input}
                        autoFocus
                        maxLength={10}
                        disabled={formLoading}
                    />
                    <small className={styles.fieldHint}>
                        Máximo 10 caracteres. Será convertido para maiúsculas automaticamente.
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja excluir o prefixo "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Gestão de Prefixos"
                actionText="Novo Prefixo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar prefixo por nome..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>{listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : currentPrefixes.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📋'}
                            title={
                                searchTerm
                                    ? 'Nenhum prefixo encontrado'
                                    : 'Nenhum prefixo cadastrado'
                            }
                            message={
                                searchTerm
                                    ? `Não encontramos resultados para "${searchTerm}"`
                                    : 'Clique em "Novo Prefixo" para começar'
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome do Prefixo</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentPrefixes.map(item => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Excluir
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="itens"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default PrefixPage;